/**
 * TabStrip — the underline tab bar used across student, teacher and admin.
 *
 * Callers pass labels + a callback; the strip owns the markup, the active
 * state class and the tablist semantics, so pages don't hand-roll
 * `<button className={`tab ${x === y ? 'active' : ''}`}>` rows.
 *
 * Styling lives in shared-ui/theme/tabs.css (.tabs / .tab). Pass `className`
 * for page-specific layout only — margins, flex-shrink, divider weight.
 */

import type { ReactNode } from 'react';

export interface TabItem<K extends string = string> {
  key: K;
  label: ReactNode;
  /** Trailing count, e.g. "Assignments (22)". Rendered inside the label row. */
  badge?: number | string;
  disabled?: boolean;
  /** Shown on hover; also the disabled explanation when `disabled`. */
  title?: string;
}

export interface TabStripProps<K extends string> {
  tabs: readonly TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  /** Page-specific layout class on the container (not typography). */
  className?: string;
  /** Accessible name for the tablist, e.g. "Assignment views". */
  ariaLabel?: string;
}

export function TabStrip<K extends string>({
  tabs, active, onChange, className, ariaLabel,
}: TabStripProps<K>) {
  return (
    <div className={`tabs ${className ?? ''}`.trim()} role="tablist" aria-label={ariaLabel}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          disabled={t.disabled}
          title={t.title}
          className={`tab ${active === t.key ? 'active' : ''}`.trim()}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.badge !== undefined && t.badge !== '' && <span className="tab-badge">{t.badge}</span>}
        </button>
      ))}
    </div>
  );
}
